import { Buffer } from 'buffer';
import Keycloak from 'keycloak-js';
import * as Sentry from "@sentry/react";



class AuthError extends Error {
  constructor({ code, message, details, success = false, ...rest }) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.success = success;
    this.details = details;
    Object.assign(this, rest);
  }
}

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'auth-keycloak' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'auth-keycloak' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'auth-keycloak',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Auth action: ${action}`,
    category: 'auth-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Auth performance: ${operation} took ${duration}ms`,
    category: 'auth-performance',
    level: 'info',
    data: { ...context, duration }
  });
};

const logSecurityEvent = (event, context = {}) => {
  Sentry.captureMessage(`Security event: ${event}`, {
    level: 'warning',
    tags: { 
      section: 'auth-security',
      event_type: 'security_event'
    },
    extra: context
  });
};

// Helper function to get CSRF token from cookie
const getCSRFToken = () => {
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return cookieValue || '';
};

// Auth hint helpers for optimistic authentication
const setAuthHint = () => {
  try {
    localStorage.setItem('auth_hint', 'true');
    logInfo('Auth hint set');
  } catch (e) {
    logError('Failed to set auth hint', {}, e);
  }
};

const clearAuthHint = () => {
  try {
    localStorage.removeItem('auth_hint');
    logInfo('Auth hint cleared');
  } catch (e) {
    logError('Failed to clear auth hint', {}, e);
  }
};

// Refresh lock to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise = null;

// Auto-refresh fetch function
const fetchWithAutoRefresh = async (url, options = {}) => {
  logInfo('Making API call with auto-refresh', { url });
  
  // Make the original request
  let response = await fetch(url, options);
  
  // If token expired (401 or 403), try to refresh and retry
  if (response.status === 401 || response.status === 403) {
    logInfo('Token expired, attempting refresh', { status: response.status });
    
    try {
      // Prevent concurrent refresh attempts
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = fetch(`${BACKEND_URL}/api/auth/oauth/refresh-token`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
      }
      
      const refreshResponse = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;
      
      if (refreshResponse.ok) {
        logInfo('Token refresh successful, retrying original request');
        // Refresh successful, retry the original request
        response = await fetch(url, options);
        
        // Verify the retry succeeded
        if (response.status === 401 || response.status === 403) {
          logError('Retry after refresh still returned 401/403');
          clearAuthHint();
          return response;
        }
      } else {
        logError('Token refresh failed', { status: refreshResponse.status });
        clearAuthHint();
        // Return response - let AuthContext handle navigation
        return response;
      }
    } catch (refreshError) {
      isRefreshing = false;
      refreshPromise = null;
      logError('Token refresh error', { error: refreshError.message });
      clearAuthHint();
      // Return original response - let AuthContext handle navigation
      return response;
    }
  }
  
  return response;
};

const BACKEND_URL = 'https://barrack.ai';
const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8180';
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'gpu-platform-realm';
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'nodeit';

const keycloakConfig = {
  url: KEYCLOAK_URL,
  realm: KEYCLOAK_REALM,
  clientId: KEYCLOAK_CLIENT_ID
};

const API_ENDPOINTS = {
  checkUser: `${BACKEND_URL}/api/auth/oauth/check-user`,
  register: `${BACKEND_URL}/api/auth/oauth/register`,
  getUserProviders: `${BACKEND_URL}/api/auth/oauth/get-user-providers`,
  getOriginalProvider: `${BACKEND_URL}/api/auth/oauth/get-original-provider`,
  oauthCallback: `${BACKEND_URL}/api/auth/oauth/callback`,
  sendLoginOTP: `${BACKEND_URL}/api/auth/oauth/send-login-otp`,
  verifyLoginOTP: `${BACKEND_URL}/api/auth/oauth/verify-login-otp`,
  forgotPassword: `${BACKEND_URL}/api/auth/oauth/forgot-password`,
  resetPassword: `${BACKEND_URL}/api/auth/oauth/reset-password`
};

const GENERIC_ERROR_MESSAGES = {
  'NETWORK_ERROR': 'Network error. Please check your connection and try again.',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again later.',
  'AUTHENTICATION_FAILED': 'Authentication failed. Please try again.',
  'VALIDATION_ERROR': 'Please check your input and try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again later.',
    'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  // Add these missing ones from backend:
  'EMAIL_ALREADY_REGISTERED': 'This email is already registered. Please sign in instead.',
  'REGISTRATION_ERROR': 'Registration failed. Please try again.',
  'EMAIL_SEND_FAILED': 'Failed to send email. Please try again later.',
  'INVALID_OTP': 'Invalid verification code. Please try again.',
  'SESSION_EXPIRED': 'Session expired. Please start over.',
  'AUTO_LOGIN_FAILED': 'Account created successfully, but auto-login failed. Please sign in manually.',
  'OTP_GENERATION_FAILED': 'Failed to generate verification code. Please try again.',
  'ACCOUNT_LOCKED': 'Account temporarily locked. Please try again later.',
  'USER_NOT_FOUND': 'User account not found.',
  'LOGIN_FAILED': 'Login failed. Please try again.',
  'PASSWORD_RESET_FAILED': 'Password reset failed. Please try again.',
  'EMAIL_NOT_FOUND': 'Email not found in our system.',
  'INVALID_CREDENTIALS': 'Invalid email or password.',
  'OAUTH_INITIALIZATION_ERROR': 'Social login initialization failed.',
  'AUTHENTICATION_INTERRUPTED': 'Authentication was interrupted.',
  'GOOGLE_SIGNIN_FAILED': 'Google sign-in failed. Please try again.',
  'MISSING_EMAIL': 'Email permission required.',
  'ACCOUNT_CREATION_FAILED': 'Account creation failed. Please try again.',
  'INVALID_REQUEST': 'Invalid request. Please try again.',
  'INVALID_RESPONSE': 'Invalid response received. Please try again.',
  'WRONG_PROVIDER': 'Please sign in using the method you originally registered with.',
  'USE_DIFFERENT_PROVIDER': 'Please use a different sign-in method.',
};

// Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Safe API response handler
const handleApiResponse = (response, operation = 'api_call') => {
  if (response.hasOwnProperty('success')) {
    return response;
  }
  
  if (response.error || response.code) {
    return {
      success: false,
      code: response.code || 'API_ERROR',
      message: response.error || response.message || 'API call failed',
      details: response
    };
  }
  
  if (response.auth_url || response.email || response.authenticated !== undefined) {
    return {
      success: true,
      ...response
    };
  }
  
  return {
    success: true,
    ...response
  };
};

// Get safe error message
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return GENERIC_ERROR_MESSAGES['SERVER_ERROR'];
  }
  
  // ✅ CHECK INTERCEPTOR FIELDS FIRST!
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  // Check backend error code first (in response data)
  if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
    return GENERIC_ERROR_MESSAGES[error.response.data.code];
  }
  
  // Check direct error code
  if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
    return GENERIC_ERROR_MESSAGES[error.code];
  }
  
  // Handle HTTP status codes
  if (error.response?.status === 401 || error.status === 401) {
    return GENERIC_ERROR_MESSAGES['AUTHENTICATION_FAILED'];
  }
  
  return GENERIC_ERROR_MESSAGES['SERVER_ERROR'];
};

class KeycloakServiceImpl {
  static keycloak = null;

  static async init() {
    const startTime = performance.now();
    
    logUserAction('keycloak_init_started', {
      existingInstance: !!this.keycloak,
      alreadyAuthenticated: this.keycloak?.authenticated || false
    });
    
    logInfo('Keycloak initialization started');

    try {
      if (this.keycloak?.authenticated) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        logPerformance('keycloak_init_existing', duration, {
          alreadyAuthenticated: true
        });
        
        logInfo('Keycloak already initialized and authenticated', {
          duration: `${duration.toFixed(2)}ms`
        });
        
        return { authenticated: true, existing: true };
      }

      if (this.keycloak) {
        try {
          logInfo('Attempting to use existing Keycloak instance');
          return { authenticated: true, existing: true };
        } catch (error) {
          logError('Existing Keycloak instance failed, clearing', {
            errorName: error.name,
            errorMessage: error.message
          }, error);
          
          this.keycloak = null;
        }
      }

      if (!this.keycloak) {
        logInfo('Creating new Keycloak instance');
        
        this.keycloak = new Keycloak({
          url: keycloakConfig.url,
          realm: keycloakConfig.realm,
          clientId: keycloakConfig.clientId,
          onLoad: 'login-required',
          enableLogging: true
        });

        const initStartTime = performance.now();
        const authenticated = await this.keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          checkLoginIframe: false,
          pkceMethod: 'S256',
          scope: 'openid profile email'
        });
        const initEndTime = performance.now();
        const initDuration = initEndTime - initStartTime;

        logPerformance('keycloak_instance_init', initDuration, {
          authenticated,
          pkceMethod: 'S256'
        });

        if (authenticated) {
          logUserAction('keycloak_authenticated', {
            initDuration: `${initDuration.toFixed(2)}ms`
          });
          
          logInfo('Keycloak authentication successful');
        } else {
          logInfo('Keycloak initialization completed without authentication');
        }

        const totalEndTime = performance.now();
        const totalDuration = totalEndTime - startTime;
        
        logPerformance('keycloak_init_complete', totalDuration, {
          authenticated,
          newInstance: true
        });

        return { authenticated, newInit: true };
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Keycloak initialization failed', {
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('keycloak_init_failed', {
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      this.clearAuthState();
      return { authenticated: false, error: getSafeErrorMessage(error) };
    }
  }

  static async handleEmailSignup(email, password) {
    const startTime = performance.now();
    
    logUserAction('email_signup_initiated', {
      email: email ? 'provided' : 'missing',
      hasPassword: !!password
    });
    
    logInfo('Email signup started', {
      emailProvided: !!email,
      passwordProvided: !!password
    });

    try {
      const registerUrl = `${BACKEND_URL}/api/auth/oauth/register`;
      logInfo('Sending registration request', { registerUrl });

      const response = await fetchWithAutoRefresh(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email),
          password
        })
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logPerformance('email_signup_failed', duration, {
          status: response.status,
          provider: 'email'
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Email signup failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          logUserAction('email_signup_failed', {
            email: sanitizeText(email),
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'REGISTRATION_FAILED',
            message: getSafeErrorMessage(errorData),
            details: errorData
          };
        } else {
          logError('Email signup failed with non-JSON response', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('email_signup_success', duration, {
        provider: 'email',
        status: data.status
      });
      
      logUserAction('email_signup_success', {
        email: sanitizeText(email),
        provider: 'email',
        status: data.status || 'REGISTERED',
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Email signup completed successfully', {
        email: sanitizeText(email),
        status: data.status || 'REGISTERED',
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("signup-success", {
        email: sanitizeText(email),
        provider: 'email',
        status: data.status || 'REGISTERED',
        completedAt: new Date().toISOString()
      });
      
      return {
        success: true,
        email: sanitizeText(data.email || email),
        provider: 'email',
        status: data.status || 'REGISTERED',
        message: data.message
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Email signup network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('email_signup_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

static async verifyRegistrationOTP(email, otp, password) {
    const startTime = performance.now();
    
    logUserAction('otp_verification_initiated', {
      email: email ? 'provided' : 'missing',
      hasOTP: !!otp
    });
    
    logInfo('OTP verification started', {
      emailProvided: !!email,
      otpProvided: !!otp
    });

    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/verify-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
      body: JSON.stringify({
  email: sanitizeText(email),
  otp: sanitizeText(otp),
  password: password
}),
        
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (!response.ok) {
        const errorData = await response.json();
        
        logError('OTP verification failed', {
          email: sanitizeText(email),
          status: response.status,
          duration: `${duration.toFixed(2)}ms`
        });
        
        return handleApiResponse({
          success: false,
          code: errorData.code || 'VERIFICATION_FAILED',
          message: getSafeErrorMessage(errorData),
          details: errorData
        });
      }

      const result = await response.json();
      
      logPerformance('otp_verification_success', duration, {
        email: sanitizeText(email)
      });

      // Set auth hint for optimistic loading on refresh
      setAuthHint();
      
      return handleApiResponse({
        success: true,
        ...result
      });
    } catch (error) {
      logError('OTP verification network error', {
        email: sanitizeText(email),
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      return handleApiResponse({
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      });
    }
  }

  static async sendLoginOTP(email) {
    const startTime = performance.now();
    
    logUserAction('send_login_otp_initiated', {
      email: email ? 'provided' : 'missing'
    });
    
    logInfo('Send login OTP started', {
      emailProvided: !!email
    });

    try {
      const response = await fetchWithAutoRefresh(API_ENDPOINTS.sendLoginOTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email)
        }),
        
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logPerformance('send_login_otp_failed', duration, {
          status: response.status,
          email: sanitizeText(email)
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Send login OTP failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'SEND_OTP_FAILED',
            message: getSafeErrorMessage(errorData),
            retry_time: errorData.retry_time,
            details: errorData
          };
        } else {
          logError('Send login OTP failed with non-JSON response', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('send_login_otp_success', duration, {
        email: sanitizeText(email)
      });
      
      logUserAction('send_login_otp_success', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Send login OTP completed successfully', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("send-login-otp-success", {
        email: sanitizeText(email),
        completedAt: new Date().toISOString()
      });
      
      return {
        success: true,
        email: sanitizeText(email),
        message: data.message || 'OTP sent successfully'
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Send login OTP network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('send_login_otp_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

  static async verifyLoginOTP(email, otp) {
    const startTime = performance.now();
    
    logUserAction('verify_login_otp_initiated', {
      email: email ? 'provided' : 'missing',
      hasOTP: !!otp
    });
    
    logInfo('Verify login OTP started', {
      emailProvided: !!email,
      otpProvided: !!otp
    });

    try {
      const response = await fetchWithAutoRefresh(API_ENDPOINTS.verifyLoginOTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email),
          otp: sanitizeText(otp)
        }),
        
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logPerformance('verify_login_otp_failed', duration, {
          status: response.status,
          email: sanitizeText(email)
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Verify login OTP failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'VERIFY_OTP_FAILED',
            message: getSafeErrorMessage(errorData),
            retry_time: errorData.retry_time,
            details: errorData
          };
        } else {
          logError('Verify login OTP failed with non-JSON response', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('verify_login_otp_success', duration, {
        email: sanitizeText(email)
      });
      
      logUserAction('verify_login_otp_success', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Verify login OTP completed successfully', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("verify-login-otp-success", {
        email: sanitizeText(email),
        completedAt: new Date().toISOString()
      });

      // Set auth hint for optimistic loading on refresh
      setAuthHint();
      
      return {
        success: true,
        email: sanitizeText(data.email || email),
        barrackId: data.barrackId,
        provider: 'email_otp',
        isAuthenticated: true,
        message: data.message || 'Login successful'
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Verify login OTP network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('verify_login_otp_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

  static async forgotPassword(email) {
    const startTime = performance.now();
    
    logUserAction('forgot_password_initiated', {
      email: email ? 'provided' : 'missing'
    });
    
    logInfo('Forgot password started', {
      emailProvided: !!email
    });

    try {
      const response = await fetchWithAutoRefresh(API_ENDPOINTS.forgotPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email)
        }),
        
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logPerformance('forgot_password_failed', duration, {
          status: response.status,
          email: sanitizeText(email)
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Forgot password failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'FORGOT_PASSWORD_FAILED',
            message: getSafeErrorMessage(errorData),
            retry_time: errorData.retry_time,
            details: errorData
          };
        } else {
          logError('Forgot password failed with non-JSON response', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('forgot_password_success', duration, {
        email: sanitizeText(email),
        status: data.status
      });
      
      logUserAction('forgot_password_success', {
        email: sanitizeText(email),
        status: data.status || 'VERIFICATION_NEEDED',
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Forgot password completed successfully', {
        email: sanitizeText(email),
        status: data.status || 'VERIFICATION_NEEDED',
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("forgot-password-success", {
        email: sanitizeText(email),
        status: data.status || 'VERIFICATION_NEEDED',
        completedAt: new Date().toISOString()
      });
      
      return {
        success: true,
        email: sanitizeText(email),
        status: data.status || 'VERIFICATION_NEEDED',
        message: data.message || 'Password reset email sent'
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Forgot password network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('forgot_password_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

  static async resendResetPasswordOtp(email) {
    const startTime = performance.now();
    
    logUserAction('resend_reset_otp_initiated', {
      email: email ? 'provided' : 'missing'
    });
    
    logInfo('Resend reset password OTP started', {
      emailProvided: !!email
    });

    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/resend-reset-otp/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email)
        })
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logPerformance('resend_reset_otp_failed', duration, {
          status: response.status,
          email: sanitizeText(email)
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Resend reset password OTP failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'SEND_OTP_FAILED',
            message: getSafeErrorMessage(errorData),
            details: errorData
          };
        } else {
          logError('Resend reset password OTP failed with non-JSON response', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('resend_reset_otp_success', duration, {
        email: sanitizeText(email)
      });
      
      logUserAction('resend_reset_otp_success', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Resend reset password OTP completed successfully', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("resend-reset-otp-success", {
        email: sanitizeText(email),
        completedAt: new Date().toISOString()
      });
      
      return {
        success: true,
        email: sanitizeText(email),
        message: data.message || 'Verification code resent successfully'
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Resend reset password OTP network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('resend_reset_otp_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

  static async resetPassword(email, otp, newPassword) {
    const startTime = performance.now();
    
    logUserAction('reset_password_initiated', {
      email: email ? 'provided' : 'missing',
      hasOTP: !!otp,
      hasPassword: !!newPassword
    });
    
    logInfo('Reset password started', {
      emailProvided: !!email,
      otpProvided: !!otp,
      passwordProvided: !!newPassword
    });

    try {
      const response = await fetchWithAutoRefresh(API_ENDPOINTS.resetPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email),
          otp: sanitizeText(otp),
          password: newPassword
        }),
        
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logPerformance('reset_password_failed', duration, {
          status: response.status,
          email: sanitizeText(email)
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Reset password failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'RESET_PASSWORD_FAILED',
            message: getSafeErrorMessage(errorData),
            retry_time: errorData.retry_time,
            details: errorData
          };
        } else {
          logError('Reset password failed with non-JSON response', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('reset_password_success', duration, {
        email: sanitizeText(email),
        autoLogin: data.autoLogin
      });
      
      logUserAction('reset_password_success', {
        email: sanitizeText(email),
        autoLogin: data.autoLogin || false,
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Reset password completed successfully', {
        email: sanitizeText(email),
        autoLogin: data.autoLogin || false,
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("reset-password-success", {
        email: sanitizeText(email),
        autoLogin: data.autoLogin || false,
        completedAt: new Date().toISOString()
      });
      
      return {
        success: true,
        email: sanitizeText(email),
        autoLogin: data.autoLogin || false,
        message: data.message || 'Password reset successful'
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Reset password network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('reset_password_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

  static async initiateSocialLogin(provider, intent = 'login') {
    const startTime = performance.now();
    
    logUserAction('social_login_initiated', {
      provider,
      intent
    });
    
    logInfo('Social login initiation started', {
      provider,
      intent,
      timestamp: Date.now()
    });

    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/${provider}/init`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
      });
    
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        logError('Social login init API call failed', {
          provider,
          intent,
          status: response.status,
          statusText: response.statusText
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          throw new AuthError({
            code: errorData.code || 'OAUTH_INITIALIZATION_ERROR',
            message: getSafeErrorMessage(errorData),
            details: errorData
          });
        } else {
          throw new AuthError({
            code: 'OAUTH_INITIALIZATION_ERROR',
            message: getSafeErrorMessage({ code: 'OAUTH_INITIALIZATION_ERROR' }),
            details: {}
          });
        }
      }
    
      const data = await response.json();
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('social_login_init_success', duration, {
        provider,
        intent,
        hasState: !!data.state,
        hasAuthUrl: !!data.auth_url
      });
      
      logInfo('Social login initialization successful', {
        provider,
        intent,
        hasState: !!data.state,
        hasAuthUrl: !!data.auth_url,
        duration: `${duration.toFixed(2)}ms`
      });
      
      logUserAction('social_login_init_success', {
        provider,
        intent,
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("oauth-initiation", {
        provider,
        intent,
        initiatedAt: new Date().toISOString()
      });
      
      return {
        success: true,
        authUrl: data.auth_url || data.authUrl,
        state: data.state
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Social login initialization error', {
        provider,
        intent,
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('social_login_init_error', {
        provider,
        intent,
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      if (error instanceof AuthError) {
        throw error;
      }
      
      throw new AuthError({
        code: error.message?.includes('Failed to fetch') ? 'NETWORK_ERROR' : 'OAUTH_INITIALIZATION_ERROR',
        message: getSafeErrorMessage({ code: error.message?.includes('Failed to fetch') ? 'NETWORK_ERROR' : 'OAUTH_INITIALIZATION_ERROR' }),
        details: error
      });
    }
  }
  
  static async handleSocialCallback(code, state, provider) {
    const startTime = performance.now();
    
    logUserAction('oauth_callback_received', {
      hasCode: !!code,
      hasState: !!state,
      provider
    });
    
    logInfo('OAuth callback processing started', {
      hasCode: !!code,
      hasState: !!state,
      provider,
      timestamp: Date.now()
    });

    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/${provider}/callback?code=${code}&state=${state}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          throw new AuthError({
            code: errorData.code || 'OAUTH_CALLBACK_FAILED',
            message: getSafeErrorMessage(errorData),
            details: errorData
          });
        } else {
          throw new AuthError({
            code: 'OAUTH_CALLBACK_FAILED',
            message: getSafeErrorMessage({ code: 'OAUTH_CALLBACK_FAILED' }),
            details: {}
          });
        }
      }

      const result = await response.json();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('oauth_callback_success', duration, {
        provider: result.authMethod || provider,
        isNewUser: result.isNewUser,
        isAuthenticated: result.isAuthenticated,
        status: result.status
      });
      
      logInfo('OAuth callback completed successfully', {
        email: sanitizeText(result.email),
        provider: result.authMethod || provider,
        status: result.status || 'SUCCESS',
        isAuthenticated: result.isAuthenticated || false,
        isNewUser: result.isNewUser || false,
        duration: `${duration.toFixed(2)}ms`
      });
      
      logUserAction('oauth_callback_success', {
        provider: result.authMethod || provider,
        isNewUser: result.isNewUser || false,
        isAuthenticated: result.isAuthenticated || false,
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("oauth-success", {
        provider: result.authMethod || provider,
        isNewUser: result.isNewUser || false,
        isAuthenticated: result.isAuthenticated || false,
        completedAt: new Date().toISOString()
      });

      setAuthHint();

      return {
        success: true,
        email: sanitizeText(result.email),
        provider: result.authMethod || provider,
        status: result.status || 'SUCCESS',
        isAuthenticated: result.isAuthenticated || false,
        isNewUser: result.isNewUser || false,
        barrackId: result.barrackId,
        sessionInfo: result.sessionInfo
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('oauth_callback_error', duration, {
        provider,
        errorName: error.name
      });
      
      // If already an AuthError, log and re-throw
      if (error instanceof AuthError) {
        logError('OAuth callback failed', { provider, code: error.code, duration: `${duration.toFixed(2)}ms` }, error);
        logUserAction('oauth_callback_failed', { provider, code: error.code, duration: `${duration.toFixed(2)}ms` });
        throw error;
      }
      
      // Step 1: Check 401 status
      if (error.response?.status === 401 || error.status === 401) {
        logError('OAuth callback authentication failed', { provider, duration: `${duration.toFixed(2)}ms` }, error);
        logUserAction('oauth_callback_auth_failed', { provider, duration: `${duration.toFixed(2)}ms` });
        
        throw new AuthError({
          code: 'AUTHENTICATION_FAILED',
          message: GENERIC_ERROR_MESSAGES['AUTHENTICATION_FAILED'],
          details: error
        });
      }
      
      // Step 2: Check backend error code
      if (error.response?.data?.code && GENERIC_ERROR_MESSAGES[error.response.data.code]) {
        logError('OAuth callback failed with backend code', { provider, code: error.response.data.code, duration: `${duration.toFixed(2)}ms` }, error);
        logUserAction('oauth_callback_failed', { provider, code: error.response.data.code, duration: `${duration.toFixed(2)}ms` });
        
        throw new AuthError({
          code: error.response.data.code,
          message: GENERIC_ERROR_MESSAGES[error.response.data.code],
          details: error
        });
      }
      
      // Step 3: Check error.code
      if (error.code && GENERIC_ERROR_MESSAGES[error.code]) {
        logError('OAuth callback failed with error code', { provider, code: error.code, duration: `${duration.toFixed(2)}ms` }, error);
        logUserAction('oauth_callback_failed', { provider, code: error.code, duration: `${duration.toFixed(2)}ms` });
        
        throw new AuthError({
          code: error.code,
          message: GENERIC_ERROR_MESSAGES[error.code],
          details: error
        });
      }
      
      // Step 4: Check network error
      if (!error.response) {
        logError('OAuth callback network error', { provider, duration: `${duration.toFixed(2)}ms` }, error);
        logUserAction('oauth_callback_network_error', { provider, duration: `${duration.toFixed(2)}ms` });
        
        throw new AuthError({
          code: 'NETWORK_ERROR',
          message: GENERIC_ERROR_MESSAGES['NETWORK_ERROR'],
          details: error
        });
      }
      
      // Step 5: Fallback
      logError('OAuth callback processing error', { provider, duration: `${duration.toFixed(2)}ms` }, error);
      logUserAction('oauth_callback_error', { provider, duration: `${duration.toFixed(2)}ms` });
      
      throw new AuthError({
        code: 'OAUTH_CALLBACK_FAILED',
        message: getSafeErrorMessage(error),
        details: error
      });
    }
  }

  static async directLogin(email, password) {
    const startTime = performance.now();
    
    logUserAction('direct_login_initiated', {
      email: email ? 'provided' : 'missing',
      hasPassword: !!password
    });
    
    logInfo('Direct login started', {
      emailProvided: !!email,
      passwordProvided: !!password
    });
  
    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        body: JSON.stringify({
          email: sanitizeText(email),
          password,
          provider: 'email'
        }),
        
      });

      const contentType = response.headers.get('content-type');
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (!response.ok) {
        logPerformance('direct_login_failed', duration, {
          provider: 'email',
          status: response.status
        });
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          
          logError('Direct login failed', {
            email: sanitizeText(email),
            status: response.status,
            duration: `${duration.toFixed(2)}ms`
          });
          
          logUserAction('direct_login_failed', {
            email: sanitizeText(email),
            duration: `${duration.toFixed(2)}ms`
          });
          
          return {
            success: false,
            code: errorData.code || 'LOGIN_FAILED',
            message: getSafeErrorMessage(errorData),
            retry_time: errorData.retry_time,
            details: errorData
          };
        } else {
          return {
            success: false,
            code: 'SERVER_ERROR',
            message: getSafeErrorMessage({ code: 'SERVER_ERROR' }),
            details: {}
          };
        }
      }

      const data = await response.json();
      
      logPerformance('direct_login_success', duration, {
        provider: 'email'
      });
      
      logInfo('Direct login successful', {
        email: sanitizeText(email),
        provider: 'email',
        status: data.status || 'SUCCESS',
        duration: `${duration.toFixed(2)}ms`
      });
      
      logUserAction('direct_login_success', {
        email: sanitizeText(email),
        provider: 'email',
        duration: `${duration.toFixed(2)}ms`
      });

      Sentry.setContext("direct-login-success", {
        email: sanitizeText(email),
        provider: 'email',
        completedAt: new Date().toISOString()
      });

      // Set auth hint for optimistic loading on refresh
      setAuthHint();
      
      return {
        success: true,
        email: sanitizeText(data.email),
        barrackId: data.barrackId,
        provider: 'email',
        status: data.status || 'SUCCESS',
        isAuthenticated: true
      };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Direct login network error', {
        email: sanitizeText(email),
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      logUserAction('direct_login_network_error', {
        email: sanitizeText(email),
        error: error.message,
        duration: `${duration.toFixed(2)}ms`
      });
      
      return {
        success: false,
        code: 'NETWORK_ERROR',
        message: getSafeErrorMessage({ code: 'NETWORK_ERROR' }),
        details: error
      };
    }
  }

  static async checkSession() {
    const startTime = performance.now();
    
    logInfo('Session check initiated');

    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/session`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        },
        
      });

      const data = await response.json();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('session_check', duration, {
        authenticated: data.authenticated
      });
      
      if (data.authenticated) {
        logInfo('Session check successful - user authenticated', {
          email: sanitizeText(data.email),
          provider: data.provider,
          preferredCurrency: data.preferred_currency || 'INR',
          duration: `${duration.toFixed(2)}ms`
        });
        
        logUserAction('session_validated', {
          provider: data.provider,
          duration: `${duration.toFixed(2)}ms`
        });

        Sentry.setContext("session-check", {
          authenticated: true,
          provider: data.provider,
          checkedAt: new Date().toISOString()
        });
      } else {
        logInfo('Session check completed - user not authenticated', {
          duration: `${duration.toFixed(2)}ms`
        });
        
        // Clear auth hint since user is not authenticated
        clearAuthHint();
      }
      
      return data;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Session check failed', {
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      // Clear auth hint since session is invalid
      clearAuthHint();
      
      return { authenticated: false };
    }
  }

  static async logout() {
    const startTime = performance.now();
    
    logUserAction('logout_initiated');
    logInfo('Logout process started');

    // Clear auth hint immediately on logout
    clearAuthHint();

    try {
      const response = await fetchWithAutoRefresh(`${BACKEND_URL}/api/auth/oauth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'X-CSRFToken': getCSRFToken(),
          'X-Requested-With': 'XMLHttpRequest'
        },
        
      });

      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('logout_complete', duration, {
        success: response.ok
      });
      
      logUserAction('logout_completed', {
        success: response.ok,
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Logout completed', {
        success: response.ok,
        duration: `${duration.toFixed(2)}ms`
      });

      return { success: true };
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logError('Logout failed', {
        duration: `${duration.toFixed(2)}ms`,
        errorName: error.name,
        errorMessage: error.message
      }, error);
      
      return { success: false, error: error.message };
    }
  }

  static clearAuthState() {
    logInfo('Clearing authentication state');
    logUserAction('auth_state_cleared');
    
    // Clear auth hint
    clearAuthHint();
    
    this.keycloak = null;
  }

  // Utility methods
  static cleanNameField(name) {
    logInfo('Cleaning name field', {
      hasName: !!name,
      originalLength: name?.length || 0
    });

    if (!name) return '';
    
    try {
      const cleaned = name
        .replace(/[^a-zA-Z\s\-']/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, 64);
      
      logInfo('Name field cleaned', {
        originalLength: name.length,
        cleanedLength: cleaned.length,
        wasModified: name !== cleaned
      });
      
      return cleaned;
    } catch (error) {
      logError('Name cleaning failed', {
        originalName: sanitizeText(name),
        errorName: error.name
      }, error);
      
      return '';
    }
  }

  static generateSecureState() {
    logInfo('Generating secure OAuth state');

    try {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      const state = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
      
      logInfo('Secure state generated', {
        stateLength: state.length
      });
      
      return state;
    } catch (error) {
      logError('Secure state generation failed', {
        cryptoAvailable: !!crypto.getRandomValues,
        errorName: error.name
      }, error);
      
      return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
  }

  static parseJwt(token) {
    logInfo('Parsing JWT token', {
      hasToken: !!token,
      tokenLength: token?.length || 0
    });

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const parsed = JSON.parse(window.atob(base64));
      
      logInfo('JWT token parsed successfully', {
        hasExp: !!parsed.exp,
        hasIss: !!parsed.iss,
        hasAud: !!parsed.aud
      });
      
      return parsed;
    } catch (error) {
      logError('JWT token parsing failed', {
        hasToken: !!token,
        tokenSegments: token?.split('.').length || 0,
        errorName: error.name
      }, error);
      
      throw this.formatError('TOKEN_PARSE_FAILED', 'Failed to parse JWT token', error);
    }
  }

  static formatError(code, message, originalError = null) {
    logInfo('Formatting standardized error', {
      code,
      hasOriginalError: !!originalError,
      originalErrorType: originalError?.constructor?.name
    });

    const error = {
      success: false,
      code,
      message: getSafeErrorMessage({ code }),
      timestamp: Date.now()
    };
  
    if (originalError) {
      if (originalError.code || originalError.details) {
        error.details = {
          ...originalError.details,
          originalCode: originalError.code,
          originalMessage: originalError.message
        };
      } else {
        error.details = {
          originalMessage: originalError.message,
          stack: originalError.stack
        };
      }
    }
    
    if (code === 'RATE_LIMIT_EXCEEDED' && originalError && originalError.retry_time) {
      error.retry_time = originalError.retry_time;
      
      logSecurityEvent('rate_limit_error_formatted', {
        code,
        retryTime: originalError.retry_time
      });
    }
    
    if (code === 'WRONG_PROVIDER' && originalError && originalError.provider) {
      error.provider = originalError.provider;
      error.action = originalError.action || 'Continue with correct provider';
    }

    logInfo('Error formatted', {
      code,
      hasDetails: !!error.details,
      hasRetryTime: !!error.retry_time,
      hasProvider: !!error.provider
    });
  
    return error;
  }

  static sanitizeName(name) {
    logInfo('Sanitizing name', {
      hasName: !!name,
      originalLength: name?.length || 0
    });

    if (!name) return '';
    
    try {
      name = name.replace(/\s*\([^)]*\)\s*/g, '');
      name = name.replace(/[^a-zA-Z\s\-'.]/g, '');
      name = name.replace(/\s+/g, ' ');
      name = name.trim();
      name = name.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      const sanitized = name || ' ';
      
      logInfo('Name sanitized', {
        originalLength: name?.length || 0,
        sanitizedLength: sanitized.length,
        wasModified: name !== sanitized
      });
      
      return sanitized;
    } catch (error) {
      logError('Name sanitization failed', {
        originalName: sanitizeText(name),
        errorName: error.name
      }, error);
      
      return ' ';
    }
  }
}

export const KeycloakService = KeycloakServiceImpl;
export default KeycloakServiceImpl;
export { fetchWithAutoRefresh, AuthError };
